import { Component, Inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LoginApiService } from '../login/store/login-api.service';
import { IAuthInfo, ILoginState, ISolutionsState, IUserAuthorizations } from '../login/store/state';
import { AppConfig } from '../shared/app.config';
import { UserAuthConfig as EligUserAuth } from '@roosevelt/elig-ui-lib';
import { UserAuthConfig as ClientUserAuth } from '@roosevelt/client-ui-lib';
import { UserAuthConfig as BillingUserAuth } from '@roosevelt/billing-lib';
import { AlertsAsync } from '../store/alerts/async';
import { EMPTY, Observable, forkJoin, from, of, TimeoutError } from 'rxjs';
import { CreateManageUserAsync } from '../store/createManageUser/async';
import { ICreateManageUserState } from '../store/createManageUser/state';
import { IAppAuthorizations, IUiAuthorizations } from 'src/app/authorizations/state';
import { AuthorizationsAsync } from 'src/app/authorizations/authorizations-async';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonUserService, Lux } from '@roosevelt/common-ui-lib/core';
import { NotificationService } from '../shared/services/notification.service';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

type TokenInfo = {
  email?: string;
  sub?: string;
  exp?: number;
  cid?: string;
};

type AdminRolesResponse = {
  users?: Array<{
    roleName?: string;
  }>;
  error?: string;
  name?: string;
};

type ClientSearchResponse = {
  clients?: unknown[];
};

type UserRoleErrorResult = {
  timedOut: boolean;
  errorMessage: string | null;
};

@Component({
  selector: 'app-oidc-callback',
  templateUrl: './oidc-callback.component.html',
  standalone: true
})
export class OidcCallbackComponent implements OnInit {
  readonly luxId = 'BMT-oidcComponent' + new Date().toISOString();
  readonly userRoleError = signal('');
  constructor(
    private readonly lux: Lux<{
      solutionsLogin: ISolutionsState,
      login: ILoginState,
      createManageUserState: ICreateManageUserState, appUiAuth: IAppAuthorizations
    }>,
    private readonly router: Router,
    private readonly loginApiService: LoginApiService,
    private readonly env: AppConfig,
    @Inject(EligUserAuth) private readonly eligUserAuth: EligUserAuth,
    @Inject(ClientUserAuth) private readonly clientUserAuth: ClientUserAuth,
    @Inject(BillingUserAuth) private readonly billingUserAuth: BillingUserAuth,
    private readonly bmtAlertsAsync: AlertsAsync,
    private readonly userRoleService: CreateManageUserAsync,
    private readonly uiAuthAsync: AuthorizationsAsync,
    private readonly commonUserService: CommonUserService,
    private readonly notificationService: NotificationService
  ) {
  }

  readonly routesMapping = {
    manageeligibility: '/eligibility/member',
    benefitsInquiry: '/benefits',
    reports: '/reports',
    billing: '/billing',
    summaryOfChanges: '/summary-of-changes',
    dentistDirectory: '/dentist',
    sat: '/sat',
    userAdmin: '/userAdmin',
    profile: '/profile/authorizations'
  };

  ngOnInit(): void {
    let hasIdToken = false;
    let impersonationUser = '';
    const hashParts = location.hash.substring(1)
      .split('&')
      .map(part => part.split(/=(.+)/));

    for (const param of hashParts) {
      sessionStorage.setItem(param[0], param[1]);
      if (param[0].indexOf('id_token') !== -1 || param[0].indexOf('access_token') !== -1) {
        hasIdToken = true;
      }
      if (param[0].indexOf('for_user') !== -1) {
        impersonationUser = param[1];
      }
    }
    let acc_token = '';
    const oktaSession = sessionStorage.getItem('okta-token-storage');
    if (oktaSession?.indexOf('idToken') > -1 || oktaSession?.indexOf('accessToken') > -1) {
      hasIdToken = true;
      acc_token = JSON.parse(oktaSession).accessToken.accessToken;
    }

    if (oktaSession?.indexOf('accessToken') > -1) {
      acc_token = JSON.parse(oktaSession).accessToken.accessToken;
    }
    const idToken = sessionStorage.getItem('impersonate_id_token') || sessionStorage.getItem('impersonate_access_token') || acc_token || sessionStorage.getItem('id_token');
    const tokenInfo = this.parseTokenInfo(idToken);

    if (!hasIdToken || !tokenInfo) {
      this.handleMissingIdToken();
      return;
    }

    this.startLoginFlow(tokenInfo, impersonationUser);
  }

  private startLoginFlow(tokenInfo: TokenInfo, impersonationUser: string): void {
    const authInfo: IAuthInfo = {
      email: this.commonUserService.claims?.email || tokenInfo.email,
      firstName: this.commonUserService.claims?.firstName,
      lastName: this.commonUserService.claims?.lastName,
      id: this.commonUserService.claims?.id || tokenInfo.sub,
      expirationTime: tokenInfo.exp,
      cid: tokenInfo.cid,
      impersonationUser: impersonationUser || ''
    };
    const userId = this.commonUserService.claims?.id || tokenInfo.sub || '';

    sessionStorage.setItem('auth-user-info', JSON.stringify(authInfo));
    this.lux.set(state => state.login, { authInfo });

    forkJoin({
      sessionId: this.ensureSessionId$(),
      userAuthorizations: this.getUserAuthorizations$(userId)
    }).subscribe();
  }

  private ensureSessionId$(): Observable<string | null> {
    const session = sessionStorage.getItem('sessionId');
    if (session) {
      return of(session);
    }

    return this.loginApiService.getSessionId().pipe(
      tap(response => {
        sessionStorage.setItem('sessionId', response);
      }),
      catchError((error: unknown) => {
        let errorMessage = 'Unable to retrieve session id';
        if (error instanceof HttpErrorResponse) {
          errorMessage = this.extractApiErrorMessage(error) || errorMessage;
        } else if (this.isTimeoutError(error)) {
          errorMessage = 'Session Id API timed out';
        }

        this.noAuthorizations(false, errorMessage);
        return EMPTY;
      })
    );
  }

  private getUserAuthorizations$(userId: string): Observable<void> {
    return this.userRoleService.getLoggedInUserRole().pipe(
      mergeMap(adminRolesResponse => {
        const userRoleErrorResult = this.captureUserRoleErrors(adminRolesResponse);
        if (userRoleErrorResult.timedOut) {
          return EMPTY;
        }
        if (userRoleErrorResult.errorMessage) {
          this.noAuthorizations(userRoleErrorResult.errorMessage);
          return EMPTY;
        }

        return this.buildAdminPlan$(adminRolesResponse as AdminRolesResponse, userId).pipe(
          mergeMap(buildAdminPlan => {
            if (!buildAdminPlan) {
              this.noAuthorizations();
              return EMPTY;
            }

            const selectedAdminPlan = this.resolveSelectedAdminPlan(buildAdminPlan);
            if (!selectedAdminPlan) {
              this.noAuthorizations();
              return EMPTY;
            }

            this.applyUserAuthorizations(buildAdminPlan, selectedAdminPlan);
            return this.loadInitialAlertsAndUiAuth$(buildAdminPlan, selectedAdminPlan, userId);
          })
        );
      }),
      map(() => void 0)
    );
  }

  private getUiAuthorizations$(adminPlan: string): Observable<void> {
    return this.uiAuthAsync.getAuthorizations('relaxed', adminPlan).pipe(
      mergeMap(uiAuth => uiAuth ? this.setTheApp$(uiAuth, adminPlan) : of(void 0))
    );
  }

  private captureUserRoleErrors(adminRolesResponse: unknown): UserRoleErrorResult {
    const normalizedResponse = adminRolesResponse as AdminRolesResponse | HttpErrorResponse | { error?: string; name?: string };
    const anyErrorsFetchingRoles = normalizedResponse?.error || normalizedResponse?.name;
    if (adminRolesResponse instanceof HttpErrorResponse && adminRolesResponse.error?.apiErrorList?.length) {
      this.userRoleError.set(adminRolesResponse.error.apiErrorList.map(item => item.errorMessage).join('\n'));
      return {
        timedOut: false,
        errorMessage: anyErrorsFetchingRoles || 'Internal server error'
      };
    }

    if (this.isTimeoutError(adminRolesResponse)) {
      this.noAuthorizations(false, 'Fetching role timed out');
      return {
        timedOut: true,
        errorMessage: null
      };
    }

    this.userRoleError.set(typeof normalizedResponse?.error === 'string' ? normalizedResponse.error : '');
    return {
      timedOut: false,
      errorMessage: anyErrorsFetchingRoles || null
    };
  }

  private buildAdminPlan$(adminRolesResponse: AdminRolesResponse, userId: string): Observable<IUserAuthorizations | null> {
    const roleName = adminRolesResponse.users?.[0]?.roleName;
    if (!roleName) {
      return of(null);
    }

    const groupOrSubgroupAdmin = this.isGroupOrSubgroupAdmin(roleName);
    return this.userRoleService.getLoggedInUserContext(groupOrSubgroupAdmin ? 3 : 2, groupOrSubgroupAdmin).pipe(
      map(response => Array.isArray(response) ? response : []),
      map(adminClients => adminClients.filter(adminPlan => adminPlan.PLAN && adminPlan.PLAN !== 'INVLDCTX')),
      tap(adminClients => {
        this.lux.set(state => state.createManageUserState.manageUser, {
          planAcronyms: adminClients.map(adminPlan => adminPlan.PLAN)
        });
      }),
      map(adminClients => {
        const userAdminPlansAndPlans = adminClients.reduce<Record<string, string[]>>((plansByAdminPlan, adminPlan) => {
          plansByAdminPlan[adminPlan.ADMINPLAN] = [...plansByAdminPlan[adminPlan.ADMINPLAN] || [], adminPlan.PLAN];
          return plansByAdminPlan;
        }, {});

        if (!Object.keys(userAdminPlansAndPlans).length) {
          return null;
        }

        return {
          userId,
          userAdminPlansAndPlans,
          roles: [],
          resources: [roleName]
        };
      })
    );
  }

  private isGroupOrSubgroupAdmin(roleName: string): boolean {
    const singleClientOrSubGroupAdmins = ['BMT-GROUP-ADMIN', 'BMT-SUBGROUP-ADMIN', 'BMT-GROUP-DELEGATE', 'BMT-SUBGROUP-DELEGATE'];
    return singleClientOrSubGroupAdmins.includes(roleName);
  }

  private resolveSelectedAdminPlan(buildAdminPlan: IUserAuthorizations): string | null {
    const planFromUrl = document.location.pathname.split('/')[1];
    const selectedAdminPlan = this.env.plansAllowed[planFromUrl];
    const adminPlans = Object.keys(buildAdminPlan.userAdminPlansAndPlans);
    return adminPlans.includes(selectedAdminPlan) && Object.keys(this.env.plansAllowed).includes(planFromUrl)
      ? selectedAdminPlan
      : null;
  }

  private applyUserAuthorizations(buildAdminPlan: IUserAuthorizations, selectedAdminPlan: string): void {
    this.lux.set(state => state.login, {
      userAuthorizations: buildAdminPlan,
      selectedAdminPlan
    });
    this.eligUserAuth.userAuthorizations = buildAdminPlan.userAdminPlansAndPlans;
    this.clientUserAuth.userAuthorizations = buildAdminPlan.userAdminPlansAndPlans;
    this.billingUserAuth.userAuthorizations = buildAdminPlan.userAdminPlansAndPlans;
  }

  private loadInitialAlertsAndUiAuth$(buildAdminPlan: IUserAuthorizations, selectedAdminPlan: string, userId: string): Observable<void> {
    const BMTAlertsReq = {
      recipient: {
        allUsers: true,
        userNames: [userId],
        memberPlanAcronyms: buildAdminPlan.userAdminPlansAndPlans[selectedAdminPlan]
      },
      adminPlanAcronyms: [selectedAdminPlan],
      applicationTypeKey: 'BMT_DENTAL',
      hideDismissedAlerts: false,
      onlyActiveAlerts: true,
      userName: userId
    };
    return this.bmtAlertsAsync.getBMTAlerts(BMTAlertsReq).pipe(
      switchMap(() => this.getUiAuthorizations$(selectedAdminPlan)),
      map(() => void 0)
    );
  }

  private setTheApp$(uiAuth: IUiAuthorizations, adminPlan: string): Observable<void> {
    // Check with where to route if 'manageEligibility' is not in authorizations.
    if (!Object.keys(uiAuth).length) {
      this.noAuthorizations();
      return EMPTY;
    }

    const leftNavFeatures = uiAuth.leftnav || [];
    const isEligInNavigation = leftNavFeatures.includes('manageeligibility');
    const targetRoute = isEligInNavigation
      ? '/eligibility/member'
      : this.resolveLeftNavRoute(leftNavFeatures);

    if (!targetRoute) {
      this.noAuthorizations();
      return EMPTY;
    }

    return from(this.router.navigateByUrl(targetRoute)).pipe(
      switchMap(() => this.loadBillingNotificationsIfNeeded$(adminPlan, leftNavFeatures)),
      switchMap(() => this.loadDdaLeftNavIfNeeded$(adminPlan, leftNavFeatures)),
      map(() => void 0)
    );
  }

  private loadBillingNotificationsIfNeeded$(adminPlan: string, leftNavFeatures: string[]): Observable<void> {
    if (adminPlan !== 'DDPMI' || !leftNavFeatures.includes('billing')) {
      return of(void 0);
    }

    return this.bmtAlertsAsync.getPaymentDueNotification().pipe(
      tap(success => {
        if (success) {
          this.notificationService.setNotificationState(true);
        }
      }),
      map(() => void 0)
    );
  }

  private loadDdaLeftNavIfNeeded$(adminPlan: string, leftNavFeatures: string[]): Observable<void> {
    if (adminPlan !== 'DDAZ') {
      return of(void 0);
    }

    return this.userRoleService.getClients(adminPlan, true).pipe(
      tap(groups => {
        if (this.hasClients(groups)) {
          this.lux.set(state => state.appUiAuth.relaxedMode, { leftnav: [...leftNavFeatures, 'lad'] });
        }
      }),
      map(() => void 0)
    );
  }

  private parseTokenInfo(token: string | null): TokenInfo | null {
    if (!token) {
      return null;
    }

    const payload = token.split('.')[1];
    if (!payload) {
      return null;
    }

    try {
      return JSON.parse(atob(payload)) as TokenInfo;
    } catch {
      return null;
    }
  }

  private handleMissingIdToken(): void {
    this.lux.set(state => state.solutionsLogin, {
      isLoginFailed: true
    });
    sessionStorage.clear();
    void this.router.navigate(['/login']);
  }

  private resolveLeftNavRoute(leftNavFeatures: string[]): string | undefined {
    const routeKey = leftNavFeatures[0] as keyof typeof this.routesMapping | undefined;
    return routeKey ? this.routesMapping[routeKey] : undefined;
  }

  private hasClients(value: unknown): value is ClientSearchResponse & { clients: unknown[] } {
    return !!value
      && typeof value === 'object'
      && Array.isArray((value as ClientSearchResponse).clients)
      && (value as ClientSearchResponse).clients.length > 0;
  }

  private extractApiErrorMessage(error: HttpErrorResponse): string {
    const apiErrorList = error.error?.apiErrorList;
    if (!apiErrorList?.length) {
      return '';
    }

    return apiErrorList.map(item => item.errorMessage).join('\n');
  }

  private isTimeoutError(error: unknown): boolean {
    return error instanceof TimeoutError
      || (error instanceof Error && error.name === 'TimeoutError')
      || (typeof error === 'object' && error !== null && 'name' in error && error.name === 'TimeoutError');
  }

  noAuthorizations(anyErrorsFetchingRoles?, timeOutError?) {
    if (timeOutError) {
      this.lux.set(state => state.solutionsLogin, {
        timeOutError: timeOutError
      });
    } else {
      this.lux.set(state => state.solutionsLogin, {
        ...!anyErrorsFetchingRoles && { noAuthroziations: true },
        ...anyErrorsFetchingRoles && { moreUserRoles: this.userRoleError() }
      });
    }
    sessionStorage.clear();
    this.router.navigate(['/notAuthorized']);
  }

}
